export interface PixelCrop {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * Load an image, draw the specified crop region onto an offscreen canvas,
 * and export as a WebP blob. Falls back to fetch-as-blob if CORS prevents
 * direct canvas access.
 */
export async function getCroppedImg(
  imageSrc: string,
  pixelCrop: PixelCrop,
): Promise<Blob> {
  try {
    return await cropWithSrc(imageSrc, pixelCrop, true);
  } catch {
    // CORS fallback: fetch as blob, create an object URL, retry
    const res = await fetch(imageSrc);
    const blob = await res.blob();
    const objectUrl = URL.createObjectURL(blob);
    try {
      return await cropWithSrc(objectUrl, pixelCrop, false);
    } finally {
      URL.revokeObjectURL(objectUrl);
    }
  }
}

function loadImage(src: string, crossOrigin: boolean): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    if (crossOrigin) img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = (e) => reject(e);
    img.src = src;
  });
}

async function cropWithSrc(
  src: string,
  crop: PixelCrop,
  crossOrigin: boolean,
): Promise<Blob> {
  const img = await loadImage(src, crossOrigin);

  const canvas = document.createElement("canvas");
  canvas.width = crop.width;
  canvas.height = crop.height;

  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Could not get canvas 2d context");

  ctx.drawImage(
    img,
    crop.x,
    crop.y,
    crop.width,
    crop.height,
    0,
    0,
    crop.width,
    crop.height,
  );

  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) resolve(blob);
        else reject(new Error("Canvas toBlob produced null"));
      },
      "image/webp",
      0.92,
    );
  });
}
